import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { ExternalTournamentWithEvents } from '@img-arena/consoles-api-streaming'
import DownloadIcon from '@mui/icons-material/Download'
import { STORE_STATUS } from '@/constants/store'
import { useImportEventsStore } from '@/stores/useImportEventsStore'

import { ImportEventsForm } from '../forms/ImportEventsForm/ImportEventsForm'
import { ActionsMenu } from '../table/actions'
import { ActionFormItem } from '../table/actions/ActionFormItem/ActionFormItem'
import { FilterableTable } from '../table/base'
import {
  getUnmappedTournamentsFilters,
  UNMAPPED_TOURNAMENTS_COLUMNS
} from './unmappedTournaments.table'

const TABLE_ID = 'unmapped-tournaments-table'

export const UnmappedTournamentsTable = () => {
  const navigate = useNavigate()

  const { data, isLoading, isFetching, fetchData, resetState, setFilters } = useImportEventsStore(
    useShallow((state) => ({
      data: state.data,
      selectedFilters: state.filters,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching,
      fetchData: state.fetchData,
      setFilters: state.setFilters,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  const unmappedTournaments = useMemo(() => {
    return data.filter((tournament) => !('exchangeTournamentId' in tournament))
  }, [data])

  const tableFilters = useMemo(
    () => getUnmappedTournamentsFilters(unmappedTournaments),
    [unmappedTournaments]
  )

  return (
    <FilterableTable
      clientSideFilters
      clientSideSortAndPagination
      loading={isLoading}
      fetching={isFetching}
      tableId={TABLE_ID}
      filters={tableFilters}
      onRowClick={(tournament) => {
        navigate(tournament.id)
      }}
      rowActions={
        <ActionsMenu>
          <ActionFormItem
            overrideText="Import"
            icon={<DownloadIcon />}
            getForm={(row: ExternalTournamentWithEvents) => (
              <ImportEventsForm tournament={row} onSubmit={fetchData} />
            )}
          />
        </ActionsMenu>
      }
      defaultSort={{ attribute: '$name', order: 'asc' }}
      onFiltersChange={setFilters}
      columns={UNMAPPED_TOURNAMENTS_COLUMNS}
      data={unmappedTournaments}
    />
  )
}
